// Klient API „Sprawdź ofertę” (`POST /api/sprawdz_oferte/`, backend_api/sprawdz_oferte.py).
// Zwraca {ok, data} albo {ok: false, error}, jak pozostałe klienty kreatorów.
import { apiFetch } from './apiFetch'
const apiUrl = process.env.REACT_APP_API_URL || '/api/'

/**
 * Wybór z komponentu `WyborSprzetu` na pola kontraktu backendu.
 * `polaMocy` mówi, pod jakim kluczem backend chce moc: moc_kw, moc_w albo pojemnosc_kwh.
 */
const sekcja = (wybor, poleMocy) => ({
  model_id: wybor?.model?.id ?? null,
  producent: wybor?.model ? null : (wybor?.producent || null),
  seria: wybor?.model ? null : (wybor?.seria || null),
  [poleMocy]: wybor?.model || !wybor?.moc ? null : Number(wybor.moc),
})

export const sprawdzOferte = async ({ falownik, fazy, magazyn, moduly, liczbaModulow, liczbaFalownikow, rozbudowa = false }) => {
  const body = {
    // Rozbudowa o magazyn: falownik tylko przy wymianie, modułów nie ma (backend przyjmuje brak sekcji).
    // `sztuk` (zgłoszenie do OSD): łańcuchy liczone dla modułów jednego z kilku falowników.
    falownik: rozbudowa && !falownik ? null : {
      ...sekcja(falownik, 'moc_kw'), fazy: fazy === '?' ? null : Number(fazy),
      ...(liczbaFalownikow > 1 ? { sztuk: liczbaFalownikow } : {}),
    },
    magazyn: magazyn?.brak
      ? { model_id: null, producent: null, seria: null, pojemnosc_kwh: null, brak: true }
      : { ...sekcja(magazyn, 'pojemnosc_kwh'), brak: false },
    moduly: rozbudowa ? null : { ...sekcja(moduly, 'moc_w'), liczba: Number(liczbaModulow) },
  }
  try {
    const res = await apiFetch(`${apiUrl}sprawdz_oferte/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await res.json().catch(() => null)
    if (!res.ok) return { ok: false, error: data?.detail || 'Nie udało się sprawdzić oferty.' }
    return { ok: true, data }
  } catch {
    return { ok: false, error: 'Brak połączenia z serwerem. Spróbuj ponownie.' }
  }
}

/** Podpowiedzi magazynów po nazwie. `szukaj_modeli` obsługuje tylko moduły i falowniki. */
export const szukajMagazynow = async (q) => {
  try {
    const res = await apiFetch(`${apiUrl}magazine_models/?search=${encodeURIComponent(q)}&limit=8`)
    if (!res.ok) return []
    const d = await res.json()
    return (d.items || []).map((m) => ({
      model_id: m.id, nazwa: m.name, producent: m.manufacturer, moc: m.capacity, ofert: m.offer_count,
      img_url: m.img_url || null,
    }))
  } catch {
    return []
  }
}

/**
 * Prośba o kontrofertę (`POST /api/v1/installers/zapytania`, aplikacja `installers`).
 * Adres liczony od REACT_APP_API_URL, jak pomiar: lokalnie działa bez osobnej zmiennej.
 */
export const poprosOKontroferte = async (dane) => {
  try {
    const res = await apiFetch(`${apiUrl}v1/installers/zapytania`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(dane),
    })
    const data = await res.json().catch(() => null)
    if (!res.ok) {
      return { ok: false, error: data?.detail || (res.status === 429
        ? 'Z tego e-maila lub telefonu wysłano już dziś kilka zapytań. Spróbuj jutro.'
        : 'Nie udało się wysłać zapytania.') }
    }
    return { ok: true, message: data?.message_pl || 'Zapytanie wysłane.' }
  } catch {
    return { ok: false, error: 'Brak połączenia z serwerem. Spróbuj ponownie.' }
  }
}

const KATEGORIA_OFERTY = { inwerter: 'falownik', panel: 'moduly', magazyn: 'magazyn' }

/**
 * Listy zależne dla wyboru „producent, seria, moc” (`GET /api/sprawdz_oferte/opcje`).
 * Liczone z tych samych modeli co raport, więc nie da się wybrać kombinacji,
 * której nie ma w katalogu. Zwraca {serie, moce, liczba} albo null przy błędzie.
 */
export const opcjeSprzetu = async (kategoria, producent, seria, moc, fazy) => {
  const par = new URLSearchParams({ kategoria: KATEGORIA_OFERTY[kategoria], producent })
  if (seria) par.set('seria', seria)
  if (moc !== '' && moc != null) par.set('moc', moc)
  if (fazy === '1' || fazy === '3') par.set('fazy', fazy)
  try {
    const res = await apiFetch(`${apiUrl}sprawdz_oferte/opcje?${par}`)
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

/**
 * Jeden model z katalogu (`GET /api/sprawdz_oferte/model`): producent, seria, moc,
 * fazy falownika i zdjęcie. Po wyborze z wyszukiwarki wypełniamy nim listy.
 * null przy błędzie: wtedy zostaje to, co przyszło z wyszukiwarki.
 */
export const modelSprzetu = async (kategoria, modelId) => {
  const par = new URLSearchParams({ kategoria: KATEGORIA_OFERTY[kategoria], model_id: modelId })
  try {
    const res = await apiFetch(`${apiUrl}sprawdz_oferte/model?${par}`)
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

// Pole mocy w odpowiedzi `GET /api/sprawdz_oferte/model` dla kategorii formularza.
const POLE_MOCY_MODELU = { inwerter: 'moc_kw', panel: 'moc_w', magazyn: 'pojemnosc_kwh' }

/**
 * Wybór z linku (model tylko z `id`) uzupełniony o nazwę, zdjęcie i moc z katalogu.
 * Wspólne dla linku „Sprawdź ofertę” (`#o=`) i narzędzia zgłoszeń (`#z=`).
 */
export const uzupelnijWybor = async (kategoria, w) => {
  if (!w?.model?.id) return w
  const k = await modelSprzetu(kategoria, w.model.id)
  if (!k) return { ...w, model: { id: w.model.id, etykieta: `Model ${w.model.id} z katalogu`, img: null } }
  const moc = k[POLE_MOCY_MODELU[kategoria]] ?? w.moc
  return {
    producent: k.producent || w.producent || '', seria: k.seria || w.seria || '', moc: moc ?? '',
    model: { id: w.model.id, etykieta: k.nazwa, producent: k.producent, moc, img: k.img_url || null, fazy: k.fazy ?? null },
  }
}

/**
 * Wgrana oferta: linie ze sprzętem -> modele z katalogu
 * (`POST /api/sprawdz_oferte/rozpoznaj_modele`). Wysyłamy WYŁĄCZNIE linie o falowniku,
 * magazynie i modułach, już bez e-maili, telefonów i numerów kont (`maskujDaneOsobowe`),
 * oraz moce odczytane w przeglądarce. Plik ani reszta tekstu oferty nie opuszczają
 * przeglądarki. null przy błędzie: formularz wypełnia się wtedy samym odczytem.
 */
export const rozpoznajModele = async (linie, moc = {}) => {
  try {
    const res = await apiFetch(`${apiUrl}sprawdz_oferte/rozpoznaj_modele`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ linie, moc }),
    })
    return res.ok ? await res.json() : null
  } catch {
    return null
  }
}

/**
 * Próbka wgranej oferty (`POST /api/v1/probki-ofert/`), tylko za zgodą użytkownika
 * i przy włączonym zapisie (`config/ofertaPlik.js`). Błędy pomijamy: to nie jest
 * funkcja dla użytkownika, tylko materiał do ulepszania odczytu.
 */
export const zapiszProbkeOferty = async (probka) => {
  try {
    await apiFetch(`${apiUrl}v1/probki-ofert/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(probka),
    })
  } catch { /* próbka nie jest potrzebna do działania strony */ }
}
